import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, Play, Film, Tv, Eye, Volume2, VolumeX, Globe, Lock, Wallet, Coins, X } from "lucide-react";

const LANG_TABS = [
  { code: "all", label: "All", native: "All" },
  { code: "en", label: "English", native: "English" },
  { code: "hi", label: "Hindi", native: "हिन्दी" },
  { code: "gu", label: "Gujarati", native: "ગુજરાતી" },
];

const LANG_LABEL = { en: "English", hi: "हिन्दी", gu: "ગુજરાતી" };
const LANG_BADGE_BG = { en: "#00E5FF", hi: "#FFD700", gu: "#A855F7" };

export function DramaList() {
  const [series, setSeries] = useState([]);
  const [lockedSeries, setLockedSeries] = useState(null); // when set, show upgrade modal
  useEffect(() => { api.get("/drama/series").then(r => setSeries(r.data)); }, []);
  return (
    <div className="px-4 py-6">
      <div className="flex items-center gap-2 mb-1">
        <Tv className="w-7 h-7 text-[#FF2A5F]"/>
        <h1 className="font-outfit font-extrabold text-3xl tracking-tighter">Vertical Drama Series</h1>
      </div>
      <p className="text-white/60 text-sm mb-3">Watch original episodic stories crafted for vertical viewing</p>
      <div className="flex items-center gap-1.5 mb-4 text-[10px] text-white/60">
        <Globe className="w-3 h-3"/>
        <span>Available in</span>
        <span className="font-bold text-[#00E5FF]">English</span>•
        <span className="font-bold text-[#FF7A00]">हिन्दी</span>•
        <span className="font-bold text-[#A855F7]">ગુજરાતી</span>
      </div>

      <div className="grid grid-cols-2 gap-3" data-testid="drama-grid">
        {series.map(s => {
          // EVERY poster is locked — tapping always opens the upgrade-wallet modal.
          return (
            <button key={s.id} type="button" onClick={() => setLockedSeries(s)}
              data-testid={`drama-card-${s.slug}`}
              className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-[#FF7A00]/30 active:scale-95 transition group text-left"
              style={{ backgroundImage: s.cover_image ? `url(${s.cover_image})` : undefined, backgroundSize: "cover", backgroundPosition: "center" }}>
              <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 40%, ${s.accent_color}66 75%, #000000ee 100%)` }} />

              <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/55 backdrop-blur text-[10px] font-bold flex items-center gap-1">
                <Film className="w-3 h-3"/>{s.episode_count} EP{s.episode_count === 1 ? "" : "S"}
              </div>

              {/* Lock button — every poster locked */}
              <div data-testid={`drama-lock-${s.slug}`}
                className="absolute top-2 right-2 w-9 h-9 rounded-full bg-[#FF7A00] text-black border-2 border-[#FF7A00] flex items-center justify-center shadow-lg">
                <Lock className="w-4 h-4"/>
              </div>

              {/* Center upgrade CTA shown on hover/tap */}
              <div className="absolute inset-0 flex items-end justify-center pb-16 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity">
                <div className="px-3 py-1.5 rounded-full bg-[#FF7A00] text-black text-[10px] font-extrabold flex items-center gap-1 shadow-2xl">
                  <Lock className="w-3 h-3"/>Upgrade wallet to unlock
                </div>
              </div>

              <div className="absolute bottom-0 inset-x-0 p-3">
                <h3 className="font-outfit font-extrabold leading-tight drop-shadow-lg" style={{ color: s.accent_color }}>{s.title}</h3>
                <p className="text-white/80 text-[10px] mt-0.5 line-clamp-2">{s.description}</p>
                <div className="mt-1.5 inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-wider text-[#FF7A00] bg-[#FF7A00]/15 border border-[#FF7A00]/30 px-2 py-0.5 rounded-full">
                  <Lock className="w-2.5 h-2.5"/>Upgrade Wallet with Goldly
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Upgrade-wallet modal */}
      {lockedSeries && (
        <div className="fixed inset-0 z-[80] bg-black/75 backdrop-blur flex items-center justify-center p-4" onClick={() => setLockedSeries(null)} data-testid="drama-upgrade-modal">
          <div onClick={e => e.stopPropagation()} className="w-full max-w-sm rounded-3xl bg-[#0B0F19] border border-[#FF7A00]/30 overflow-hidden">
            <div className="relative h-32" style={{ backgroundImage: lockedSeries.cover_image ? `url(${lockedSeries.cover_image})` : undefined, backgroundSize: "cover", backgroundPosition: "center" }}>
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/60 to-[#0B0F19]"/>
              <button onClick={() => setLockedSeries(null)} className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50"><X className="w-4 h-4"/></button>
              <div className="absolute bottom-2 left-3 right-3">
                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF7A00]/15 border border-[#FF7A00]/40 text-[#FF7A00] text-[9px] font-extrabold uppercase tracking-wider mb-1"><Lock className="w-2.5 h-2.5"/>Premium drama</div>
                <div className="font-outfit font-extrabold text-lg leading-tight">{lockedSeries.title}</div>
              </div>
            </div>
            <div className="p-5 text-center">
              <Coins className="w-10 h-10 text-[#FF7A00] mx-auto mb-2"/>
              <h3 className="font-outfit font-extrabold text-xl mb-1">Upgrade your wallet</h3>
              <p className="text-sm text-white/70 mb-4">Top up your Goldly to unlock this drama series and binge every episode.</p>
              <div className="flex flex-col gap-2">
                <Link to="/wallet" onClick={() => setLockedSeries(null)} data-testid="drama-upgrade-cta"
                  className="w-full py-3 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#FF7A00] text-black font-extrabold flex items-center justify-center gap-2 active:scale-95">
                  <Wallet className="w-4 h-4"/>Upgrade Wallet
                </Link>
                <button onClick={() => setLockedSeries(null)} className="w-full py-2.5 rounded-full bg-white/5 text-white/70 font-bold text-sm">Maybe later</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function DramaSeries() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [series, setSeries] = useState(null);
  const [eps, setEps] = useState([]);
  const [lang, setLang] = useState("all");
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    api.get(`/drama/series/${slug}`).then(r => setSeries(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    const q = lang === "all" ? "" : `?language=${lang}`;
    api.get(`/drama/series/${slug}/episodes${q}`).then(r => {
      setEps(r.data);
      setCurrent(r.data.length > 0 ? r.data[0] : null);
    }).catch(() => { setEps([]); setCurrent(null); });
  }, [slug, lang]);

  const playEp = (ep) => {
    setCurrent(ep);
    setPlaying(true);
    api.post(`/drama/episodes/${ep.id}/view`).catch(() => {});
    setTimeout(() => { if (videoRef.current) { videoRef.current.currentTime = 0; videoRef.current.play().catch(()=>{}); } }, 100);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) { videoRef.current.play(); setPlaying(true); }
    else { videoRef.current.pause(); setPlaying(false); }
  };

  if (!series) return <div className="px-4 py-10 text-center text-white/50">Loading…</div>;

  return (
    <div className="px-4 py-6">
      <button onClick={() => nav(-1)} data-testid="drama-back" className="flex items-center gap-1 text-sm text-white/70 mb-3"><ArrowLeft className="w-4 h-4"/>Back</button>
      <h1 className="font-outfit font-extrabold text-2xl mb-1" style={{ color: series.accent_color }}>{series.title}</h1>
      <p className="text-white/70 text-sm mb-3">{series.description}</p>

      {/* Language tabs */}
      <div className="flex items-center gap-2 mb-4 overflow-x-auto no-scrollbar" data-testid="drama-lang-tabs">
        {LANG_TABS.map(t => (
          <button key={t.code} onClick={() => setLang(t.code)} data-testid={`lang-tab-${t.code}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap border transition ${lang === t.code
              ? "bg-[#00E5FF] text-black border-[#00E5FF]"
              : "bg-white/5 text-white/70 border-white/10 hover:border-white/30"}`}>
            {t.native}
          </button>
        ))}
      </div>

      {current ? (
        <div className="relative aspect-[9/16] max-h-[70vh] rounded-3xl overflow-hidden bg-black mb-4 mx-auto max-w-sm" data-testid="drama-player">
          <video ref={videoRef} src={current.video_url} autoPlay playsInline muted={muted} controls={false}
            className="absolute inset-0 w-full h-full object-contain bg-black" onClick={togglePlay}
            onEnded={() => {
              const idx = eps.findIndex(e => e.id === current.id);
              if (idx >= 0 && idx + 1 < eps.length) playEp(eps[idx + 1]);
              else setPlaying(false);
            }}/>
          <div className="absolute top-2 left-2 right-2 flex items-center justify-between z-10">
            <span className="px-2 py-1 rounded-full bg-black/60 backdrop-blur text-[10px] font-bold">EP {current.ep_number} • {current.title}</span>
            <button onClick={() => setMuted(m => !m)} data-testid="drama-mute" className="w-8 h-8 rounded-full bg-black/55 backdrop-blur flex items-center justify-center">
              {muted ? <VolumeX className="w-4 h-4"/> : <Volume2 className="w-4 h-4"/>}
            </button>
          </div>
          {!playing && (
            <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center bg-black/30">
              <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center"><Play className="w-7 h-7 text-black fill-black ml-1"/></div>
            </button>
          )}
        </div>
      ) : (
        <div className="aspect-[9/16] max-h-[60vh] rounded-3xl bg-white/5 border border-white/10 mb-4 max-w-sm mx-auto flex items-center justify-center text-white/40 text-sm">
          {lang === "all" ? "No episodes uploaded yet" : `No ${LANG_LABEL[lang] || lang} episodes yet`}
        </div>
      )}

      <h2 className="font-outfit font-bold text-base mb-2">Episodes ({eps.length})</h2>
      <div className="space-y-2" data-testid="ep-list">
        {eps.map(ep => (
          <button key={ep.id} onClick={() => playEp(ep)} data-testid={`ep-${ep.ep_number}`}
            className={`w-full flex items-center gap-3 p-2 rounded-2xl border ${current?.id === ep.id ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-white/10 bg-white/5"} active:scale-[0.98]`}>
            {ep.thumbnail_url ? (
              <img src={ep.thumbnail_url} alt="" className="w-16 h-20 rounded-lg object-cover"/>
            ) : (
              <div className="w-16 h-20 rounded-lg bg-white/10 flex items-center justify-center"><Film className="w-5 h-5 text-white/40"/></div>
            )}
            <div className="flex-1 min-w-0 text-left">
              <div className="flex items-center gap-1.5">
                <div className="text-xs font-bold text-white/90">EP {ep.ep_number}</div>
                {ep.language && (
                  <span className="px-1.5 py-0 rounded-full text-[9px] font-extrabold tracking-wider"
                    style={{ background: `${LANG_BADGE_BG[ep.language] || "#888"}22`, color: LANG_BADGE_BG[ep.language] || "#aaa", border: `1px solid ${LANG_BADGE_BG[ep.language] || "#888"}55` }}>
                    {LANG_LABEL[ep.language] || ep.language.toUpperCase()}
                  </span>
                )}
              </div>
              <div className="text-sm font-semibold truncate" style={{ color: series.accent_color }}>{ep.title}</div>
              <div className="text-[10px] text-white/50 flex items-center gap-1 mt-0.5"><Eye className="w-3 h-3"/>{ep.views || 0}{ep.duration_seconds ? ` • ${Math.floor(ep.duration_seconds / 60)}:${String(ep.duration_seconds % 60).padStart(2,"0")}` : ""}</div>
            </div>
          </button>
        ))}
        {eps.length === 0 && <p className="text-center text-white/40 text-sm py-6">Episodes coming soon — stay tuned!</p>}
      </div>
    </div>
  );
}
